import React, { useEffect, useState, useRef, useCallback } from "react";
import { Tooltip as TooltipLib } from "@npm_leadtech/qr-ui-components";

import { PickerOption, PickerProps } from "@/components/Picker/types";
import PickerItem from "@/components/Picker/PickerItem";

import { QREditorContext } from "@/contexts/QREditorContext";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";

import { ConnectForm } from "@/utils/formHelper";
import { useDraggableScroll } from "@/hooks/ui";

import "./styles.scss";

export function FramePicker({
  title,
  options,
  multiple,
  id,
  dataQA,
  defaultValue,
  className,
  selector,
  onClick,
  defaultSelected,
}: PickerProps) {
  const defaultOption = defaultSelected || defaultValue;
  const [selectedOption, setSelectedOption] = useState<string | string[]>(defaultOption);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { qrData } = React.useContext<QREditorContextProps<any>>(QREditorContext);

  const { ref, onMouseDown, onMouseLeave, onMouseUp, onMouseMove } =
    useDraggableScroll<HTMLUListElement>();

  // Track the last value we sent via onClick to prevent retriggering (race condition thart was proviking the loop)
  const lastSentValueRef = useRef<string | string[]>(defaultOption);
  // Track if we're handling a user initiated change
  const isUserChangeRef = useRef(false);

  const handleSetSelectedOption = useCallback((value: string | string[]) => {
    isUserChangeRef.current = true;
    setSelectedOption(value);
  }, []);

  // Here qwe sync from context (ignore if it's triggering our own change)
  useEffect(() => {
    if (id === "frameStylePicker") {
      const contextValue = qrData.qrStyle.frameStyle;
      if (contextValue !== lastSentValueRef.current && contextValue !== selectedOption) {
        setSelectedOption(contextValue);
      }
    }
  }, [qrData.qrStyle.frameStyle, id, selectedOption]);

  // Notify parent of selection changes
  useEffect(() => {
    if (onClick && isUserChangeRef.current) {
      lastSentValueRef.current = selectedOption;
      onClick(selectedOption);
    }
    isUserChangeRef.current = false;
  }, [selectedOption, onClick]);

  return (
    <ConnectForm>
      {title && <p>{title}</p>}
      <ul
        ref={ref}
        onMouseDown={onMouseDown}
        onMouseLeave={onMouseLeave}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        data-testid="FramePicker"
        data-qa={dataQA}
        className={`FramePicker${className ? " " + className : ""}`}
        id={id}
      >
        {options.map((option: string | PickerOption, i: number) => {
          const formattedOption = typeof option === "string" ? option : option.pickerItem;
          if (typeof option !== "string" && option.tooltip) {
            return (
              <React.Fragment key={i}>
                <div>
                  <TooltipLib
                    dataQA="framePicker-tooltip"
                    dataTestId="tooltip"
                    text={option.tooltip?.text}
                    position={option.tooltip?.position}
                  >
                    <PickerItem
                      option={formattedOption}
                      label={option?.label}
                      id={i}
                      multiple={multiple}
                      selectedOption={selectedOption}
                      setSelectedOption={handleSetSelectedOption}
                      selector={selector}
                    />
                  </TooltipLib>
                </div>
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={i}>
              <PickerItem
                option={formattedOption}
                label={typeof option !== "string" && option?.label}
                id={i}
                multiple={multiple}
                selectedOption={selectedOption}
                setSelectedOption={handleSetSelectedOption}
                selector={selector}
              />
            </React.Fragment>
          );
        })}
      </ul>
    </ConnectForm>
  );
}
